import { useState, useEffect, useCallback } from 'react';
import { useAccount, useSignMessage, useDisconnect } from 'wagmi';
import { authService } from '../services/authService';

/**
 * 钱包认证Hook - 集成wagmi和后端API
 */
export function useWalletAuth() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();
  
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [user, setUser] = useState(null);

  // 检查本地存储的认证状态
  useEffect(() => {
    const checkAuthStatus = () => {
      if (authService.isLoggedIn()) {
        const storedUser = authService.getUser();
        if (storedUser && storedUser.wallet_address?.toLowerCase() === address?.toLowerCase()) {
          setIsAuthenticated(true);
          setUser(storedUser);
        } else {
          // 钱包地址不匹配，清除认证状态
          authService.logout();
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    if (isConnected && address) {
      checkAuthStatus();
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [address, isConnected]);

  /**
   * 钱包签名登录
   */
  const signInWithWallet = useCallback(async () => {
    // 更严格的状态检查
    if (!address || !isConnected) {
      const errorMsg = 'Please connect your wallet first';
      setAuthError(errorMsg);
      throw new Error(errorMsg);
    }

    // 检查是否已经在认证中
    if (isAuthenticating) {
      // console.log('⚠️ 已在认证中，跳过重复请求');
      return;
    }

    setIsAuthenticating(true);
    setAuthError(null);

    try {
      // 在每个关键步骤前检查连接状态
      if (!isConnected || !address) {
        throw new Error('Wallet disconnected during authentication');
      }

      // 1. 获取nonce
      // console.log('🔄 获取签名nonce...');
      const nonceData = await authService.getNonce(address);
      
      // 再次检查连接状态
      if (!isConnected || !address) {
        throw new Error('Wallet disconnected during nonce retrieval');
      }
      
      // 2. 签名消息
      // console.log('🔄 请求钱包签名...');
      const signature = await signMessageAsync({
        message: nonceData.message
      });

      // 签名后再次检查连接状态
      if (!isConnected || !address) {
        throw new Error('Wallet disconnected during signing');
      }

      // 3. 后端登录验证
      // console.log('🔄 验证签名并登录...');
      const loginData = await authService.walletLogin(
        address,
        signature,
        nonceData.message
      );

      // 4. 更新状态
      setIsAuthenticated(true);
      setUser(loginData.user);
      setAuthError(null); // 清除之前的错误
      
      // console.log('✅ 钱包登录成功:', loginData.user);
      return loginData;

    } catch (error) {
      // console.error('❌ 钱包登录失败:', error);
      
      // 设置错误消息
      let errorMessage = error.message;
      
      if (error.message?.includes('User rejected')) {
        errorMessage = 'User cancelled the signature';
      } else if (error.message?.includes('User denied')) {
        errorMessage = 'User denied the signature';
      } else if (error.message?.includes('422')) {
        errorMessage = 'Signature verification failed, please try again';
      } else if (error.message?.includes('409') || error.status === 409) {
        errorMessage = 'Already logged in on another device, please logout first';
      }
      
      setAuthError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      
      // 所有错误情况都自动断开钱包连接，回到Connect Wallet状态
      // console.log('🚨 登录错误，自动断开钱包连接:', errorMessage);
      try {
        disconnect();
        // console.log('✅ 已自动断开钱包连接');
      } catch (disconnectError) {
        // console.error('⚠️ 断开钱包连接失败:', disconnectError);
      }
      
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, [address, isConnected, signMessageAsync, disconnect]);

  /**
   * 登出
   */
  const signOut = useCallback(async () => {
    // console.log('🔌 开始登出流程...');
    
    try {
      // 尝试调用后端登出 API
      const response = await authService.logout();
      // console.log('✅ 后端登出成功:', response);
      
    } catch (error) {
      console.error('⚠️ 后端登出失败:', error);
      
      // 记录错误类型用于调试
      if (error.message?.includes('500')) {
        // console.error('🚨 服务器内部错误，可能是数据库操作失败');
      } else if (error.message?.includes('Failed to fetch')) {
        // console.error('🚨 网络连接失败，后端服务可能不可用');
      } else if (error.message?.includes('CORS')) {
        // console.error('🚨 CORS错误，跨域请求被阻止');
      }
      
      // 即使后端登出失败，也继续清除前端状态
      // 这确保用户不会卡在登录状态
    } finally {
      // 无论后端操作是否成功，都要清除前端状态
      // console.log('🧹 清除前端认证状态...');
      
      setIsAuthenticated(false);
      setUser(null);
      setAuthError(null);
      
      // 断开钱包连接，防止自动重新登录
      try {
        disconnect();
        // console.log('✅ 已断开钱包连接');
      } catch (disconnectError) {
        // console.error('⚠️ 断开钱包连接失败:', disconnectError);
      }
      
      // 清除本地存储的token
      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        // console.log('✅ 本地存储清除成功');
      } catch (storageError) {
        // console.error('⚠️ 清除本地存储失败:', storageError);
      }
      
      // console.log('✅ 登出流程完成');
    }
  }, [disconnect]);

  /**
   * 清除错误状态
   */
  const clearError = useCallback(() => {
    setAuthError(null);
  }, []);

  /**
   * 刷新用户信息
   */
  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (error) {
      // console.error('❌ 刷新用户信息失败:', error);
      // 如果刷新失败，可能是token过期，清除认证状态
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    }
  }, [isAuthenticated]);

  /**
   * 自动登录（钱包连接后自动签名登录）
   */
  const autoLogin = useCallback(async () => {
    // 更严格的状态检查，防止在不合适的时机执行
    if (!isConnected || !address || isAuthenticated || isAuthenticating) {
      // console.log('🚫 autoLogin 跳过：', {
      //   isConnected,
      //   hasAddress: !!address,
      //   isAuthenticated,
      //   isAuthenticating
      // });
      return;
    }

    // console.log('🔄 开始 autoLogin 流程...');

    // 等待一小段时间确保状态稳定
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 再次检查状态，防止在状态变化过程中执行
    if (!isConnected || !address || isAuthenticated || isAuthenticating) {
      // console.log('🚫 autoLogin 终止：状态已变化');
      return;
    }

    // 首先检查是否有有效的认证信息
    if (authService.isLoggedIn()) {
      const storedUser = authService.getUser();
      if (storedUser?.wallet_address?.toLowerCase() === address.toLowerCase()) {
        try {
          // 验证token是否仍然有效
          const userData = await authService.getCurrentUser();
          setIsAuthenticated(true);
          setUser(userData);
          // console.log('✅ 自动登录成功（使用存储的token）');
          return;
        } catch (error) {
          // console.log('⚠️ 存储的token无效，清除状态');
          // Token无效，清除状态
          authService.logout();
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    }

    // 如果没有有效token，自动触发签名登录
    try {
      // 再次检查状态，防止在异步操作过程中状态发生变化
      if (!isConnected || !address || isAuthenticated || isAuthenticating) {
        // console.log('⚠️ autoLogin 中断：状态已变化');
        return;
      }
      
      // console.log('🔄 钱包已连接，自动开始签名登录...');
      await signInWithWallet();
    } catch (error) {
      // console.log('⚠️ 自动签名登录失败:', error.message);
      // signInWithWallet 已经处理了所有错误情况，包括自动断开连接
      // 这里不需要额外处理
    }
  }, [isConnected, address, isAuthenticated, isAuthenticating, signInWithWallet, disconnect]);

  // 钱包连接时尝试自动登录（防抖机制）
  useEffect(() => {
    if (isConnected && address && !isAuthenticating && !isAuthenticated) {
      // 使用延迟执行防止频繁触发
      const timer = setTimeout(() => {
        // 再次检查状态
        if (isConnected && address && !isAuthenticating && !isAuthenticated) {
          autoLogin();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, address, isAuthenticating, isAuthenticated, autoLogin]);

  // 钱包断开时清除认证状态
  useEffect(() => {
    if (!isConnected) {
      if (isAuthenticated) {
        // console.log('🔌 钱包断开连接，清除认证状态');
        signOut();
      }
      // 清除任何可能的错误状态
      setAuthError(null);
    }
  }, [isConnected, isAuthenticated, signOut]);

  return {
    // 状态
    isAuthenticated,
    isAuthenticating,
    authError,
    user,
    
    // 方法
    signInWithWallet,
    signOut,
    refreshUser,
    autoLogin,
    clearError,
    
    // 钱包状态
    walletAddress: address,
    isWalletConnected: isConnected,
    
    // 便捷方法
    isLoggedIn: isAuthenticated && isConnected,
    needsSignIn: isConnected && !isAuthenticated,
  };
}
